<template>
  <div class="col-sm-10 col-md-12 col-lg-8">
    <div class="dashboard-widget mb-30">
      <div class="dash-pro-item">
        <div class="header d-flex justify-content-between align-items-center">
          <h4 class="title">Segurança da Conta</h4>

        </div>
        <div class="dash-pro-body">
          <div v-if="loading" class="mfa-loading">
            <i class="fa fa-spin fa-spinner"></i>
            Carregando configuração do 2FA...
          </div>

          <div class="alert alert-info" v-if="setup && !isMfaEnabled">
            A Autenticação de 2 Fatores (2FA) usa códigos TOTP compatíveis com Google Authenticator. Quando ativada, o
            login do site só é concluído após o OTP ou o
            código de recuperação.
          </div>

          <div class="mfa-summary" v-if="setup">
            <div>
              <strong>Status:</strong>
              <span v-if="setup" :class="['mfa-status-badge', isMfaEnabled ? 'is-enabled' : 'is-disabled']">
                {{ isMfaEnabled ? 'Autenticação de 2 fatores ativado' : 'Autenticação de 2 fatores desativado' }}
              </span>
            </div>
            <div v-if="isMfaEnabled">
              <strong>Códigos de recuperação restantes:</strong>
              {{ setup.recoveryCodesLeft }}
            </div>
          </div>

          <div class="mfa-step-card" v-if="setup && !isMfaEnabled">
            <div class="mfa-step-title">
              <span class="mfa-step-number">1</span>
              <div>
                <h5>Instale o aplicativo</h5>
                <p>Instale o Google Authenticator no celular antes de seguir com a configuração.</p>
              </div>
            </div>

            <div class="security-actions">
              <a class="custom-button action-button" :href="androidUrl" target="_blank"
                rel="noopener noreferrer">Android</a>
              <a class="custom-button action-button" :href="iosUrl" target="_blank" rel="noopener noreferrer">iOS</a>
            </div>
          </div>

          <div class="mfa-step-card" v-if="setup && !isMfaEnabled">
            <div class="mfa-step-title">
              <span class="mfa-step-number">2</span>
              <div>
                <h5>Leia o QR Code</h5>
                <p>Abra o aplicativo autenticador e escaneie o QR Code abaixo.</p>
              </div>
            </div>

            <div class="mfa-qr-wrapper">
              <qrcode-vue v-if="setup.authenticatorUri" :value="setup.authenticatorUri" :size="220" level="H"
                class="mfa-qr" />
              <div v-else class="text-muted">Não foi possível gerar o QR Code.</div>
            </div>
          </div>

          <div class="mfa-step-card" v-if="setup && !isMfaEnabled">
            <div class="mfa-step-title">
              <span class="mfa-step-number">3</span>
              <div>
                <h5>Ou copie a chave manual</h5>
                <p>Se preferir, digite esta chave manualmente no aplicativo autenticador.</p>
              </div>
            </div>

            <div class="mfa-key-group">
              <input class="form-control" type="text" :value="setup.sharedKey" readonly />
              <button class="custom-button action-button small-button" @click="copiarChave" type="button">Copiar
                chave</button>
            </div>
            <small class="text-muted">{{ copyStatus || 'Espaços e letras maiúsculas/minúsculas não importam.' }}</small>
          </div>

          <div class="mfa-step-card" v-if="setup && !isMfaEnabled">
            <div class="mfa-step-title">
              <span class="mfa-step-number">4</span>
              <div>
                <h5>Confirme o código gerado</h5>
                <p>Digite o código exibido no aplicativo para ativar o 2FA nesta conta.</p>
              </div>
            </div>

            <div class="mfa-verify-form">
              <input v-model="codigo" type="text" class="form-control" maxlength="7"
                placeholder="Código de autenticação" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9 ]*"
                @input="formatarCodigo" />
              <button class="custom-button action-button" @click="ativarMfa" :disabled="saving" type="button">
                <i class="fa fa-spin fa-spinner" v-if="saving"></i>
                Verificar
              </button>
            </div>
          </div>

          <div class="mfa-step-card" v-if="isMfaEnabled">
            <div class="mfa-step-title">
              <div>
                <h5>Autenticação em dois fatores</h5>
                <p>Utilize o botão abaixo caso deseje desativar.</p>
              </div>
            </div>

            <div class="security-actions">
              <button class="custom-button action-button action-danger" @click="desativarMfa" :disabled="disabling"
                type="button">
                <i class="fa fa-spin fa-spinner" v-if="disabling"></i>
                Desativar
              </button>
            </div>
          </div>

          <div class="alert alert-success recovery-alert" v-if="generatedRecoveryCodes.length">
            <strong>Guarde estes códigos de recuperação.</strong>
            <p>Eles são exibidos apenas agora e podem ser usados para entrar caso você fique sem acesso ao autenticador.
            </p>
            <div class="recovery-actions mb-3 mt-2">
              <button class="custom-button action-button small-button" @click="copiarRecoveryCodes" type="button">
                <i class="fa fa-copy"></i> Copiar todos
              </button>
              <button class="custom-button action-button small-button action-secondary ml-2"
                @click="baixarRecoveryCodes" type="button">
                <i class="fa fa-download"></i> Baixar (.txt)
              </button>
            </div>
            <ul class="recovery-list">
              <li v-for="code in generatedRecoveryCodes" :key="code">{{ code }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import http from '@/service/http/http';
import notification from '@/mixins/notification';

export default {
  name: 'SegurancaConta',
  components: {
    QrcodeVue,
  },
  mixins: [notification],
  data() {
    return {
      loading: false,
      saving: false,
      disabling: false,
      codigo: '',
      copyStatus: '',
      setup: null,
      generatedRecoveryCodes: [],
      androidUrl: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=pt_BR',
      iosUrl: 'https://apps.apple.com/br/app/google-authenticator/id388497605',
    };
  },
  computed: {
    ...mapGetters(['getUsuarioInfo']),
    isMfaEnabled() {
      return !!(this.setup && this.setup.mfaEnabled);
    },
  },
  methods: {
    ...mapActions(['info']),
    formatarCodigo() {
      const apenasDigitos = String(this.codigo || '').replace(/\D/g, '').slice(0, 6);
      if (apenasDigitos.length <= 3) {
        this.codigo = apenasDigitos;
      } else {
        this.codigo = `${apenasDigitos.slice(0, 3)} ${apenasDigitos.slice(3)}`;
      }
    },
    getMensagemErroAmigavel(error, contexto = 'carregar a configuração do 2FA') {
      if (!error) {
        return `Não foi possível ${contexto}. Tente novamente em alguns instantes.`;
      }
      const status = error.response?.status;
      const payload = error.response?.data || {};
      const errorsDict = payload.errors || payload.Errors || payload.erros || {};
      const hasUnauthorizedKey =
        !!errorsDict.unauthorized || !!errorsDict.token_invalido;
      const msg = payload.message || payload.Message || payload.mensagem;

      // Sessão inválida / expirada / token de outro ambiente
      if (status === 401 || status === 403 || hasUnauthorizedKey) {
        return 'Sua sessão expirou ou não é válida neste ambiente. Por favor, saia e faça login novamente.';
      }

      // AppException da API com mensagem legível (ex: "Usuário não encontrado no Identity.")
      if (msg && typeof msg === 'string' && !/^Request failed with status code/.test(msg)) {
        const temHtml = /[<>]/.test(msg) || msg.includes('Unexpected character');
        if (temHtml || msg.toLowerCase().includes('parse') || msg.toLowerCase().includes('json reader')) {
          return 'Sessão inválida. Saia, faça login novamente e, se o problema persistir, contate o nosso suporte.';
        }
        return msg;
      }

      // InnerException contendo erro técnico de parse HTML
      const exc = errorsDict.Exception || errorsDict.exception || errorsDict.innerException;
      if (exc && typeof exc === 'string' && (exc[0] === '<' || exc.toLowerCase().includes('path'))) {
        return 'Sessão inválida. Saia, faça login novamente e, se o problema persistir, contate o nosso suporte.';
      }

      // Axios genérico "Request failed with status code 400"
      if (error.message && /^Request failed with status code \d+$/.test(error.message)) {
        const code = status ? ` (HTTP ${status})` : '';
        return `Não foi possível ${contexto}${code}. Saia, faça login novamente e, se o problema persistir, contate o nosso suporte.`;
      }

      if (error.message) {
        const temHtml = /[<>]/.test(error.message) || error.message.includes('Unexpected character');
        if (temHtml) {
          return 'Sessão inválida. Saia, faça login novamente e, se o problema persistir, contate o nosso suporte.';
        }
        return error.message;
      }

      return `Não foi possível ${contexto}. Tente novamente em alguns instantes.`;
    },
    async _tratarErroMfa(error, contexto = 'carregar a configuração do 2FA') {
      const mensagem = this.getMensagemErroAmigavel(error, contexto);
      const status = error?.response?.status;
      const errorsDict = (error?.response?.data?.errors)
        || (error?.response?.data?.Errors)
        || {};
      const sessaoInvalida = (status === 401 || status === 403)
        || !!errorsDict.unauthorized
        || !!errorsDict.token_invalido
        || /sessão expirou|sessão inválida/i.test(mensagem);

      if (sessaoInvalida) {
        await this.showAlert({
          type: 'warning',
          title: 'Sessão expirada',
          message: mensagem,
          confirmButtonText: 'Fazer login novamente',
          showConfirmButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: true,
        }).catch(() => { /* garante que prossegue mesmo se fechar por fora */ });
        try {
          await this.$store.dispatch('login/logout');
        } catch (_) { void _; }
        try {
          await this.$router.replace({ name: 'login' });
        } catch (_) { void _; }
        return;
      }

      const title = contexto.startsWith('ativar')
        ? 'Erro ao ativar 2FA'
        : contexto.startsWith('desativar')
          ? 'Erro ao desativar 2FA'
          : 'Erro ao carregar 2FA';

      await this.showAlert({
        type: 'error',
        title,
        message: mensagem,
        showConfirmButton: true,
      }).catch(() => { });
    },
    async carregarSetup() {
      this.loading = true;
      try {
        const response = await http.get('usuario/Mfa/Setup');
        if (!response.data.succeeded) {
          throw new Error(response.data.message || 'Não foi possível carregar a configuração do 2FA.');
        }

        this.setup = response.data.data;
      } catch (error) {
        await this._tratarErroMfa(error, 'carregar a configuração do 2FA');
      } finally {
        this.loading = false;
      }
    },
    async copiarChave() {
      const key = this.setup?.sharedKey;
      if (!key) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(key);
        } else {
          const input = document.createElement('input');
          input.value = key;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }

        this.copyStatus = 'Chave copiada com sucesso.';
      } catch (_) {
        this.copyStatus = 'Não foi possível copiar automaticamente. Copie a chave manualmente.';
      }
    },
    async copiarRecoveryCodes() {
      const text = this.generatedRecoveryCodes.join('\n');
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const input = document.createElement('textarea');
          input.value = text;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        this.showToast({ type: 'success', title: 'Códigos copiados com sucesso.' });
      } catch (err) {
        this.showAlert({ type: 'error', title: 'Erro', message: 'Não foi possível copiar os códigos.', showConfirmButton: true });
      }
    },
    baixarRecoveryCodes() {
      const text = "Códigos de Recuperação 2FA:\n\n" + this.generatedRecoveryCodes.join('\n');
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mfa-recovery-codes.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async ativarMfa() {
      const codigoDigits = String(this.codigo || '').replace(/\D/g, '');
      if (!codigoDigits || codigoDigits.length !== 6) {
        this.showAlert({
          type: 'warning',
          title: 'Código obrigatório',
          message: 'Informe o código de 6 dígitos exibido no aplicativo autenticador.',
          showConfirmButton: true,
        });
        return;
      }

      this.saving = true;
      try {
        const response = await http.post('usuario/Mfa/Enable', { code: codigoDigits });
        if (!response.data.succeeded) {
          throw new Error(response.data.message || 'Não foi possível ativar o 2FA.');
        }

        this.generatedRecoveryCodes = response.data.data?.recoveryCodes || [];
        this.setup = {
          ...this.setup,
          mfaEnabled: true,
          recoveryCodesLeft: this.generatedRecoveryCodes.length || this.setup.recoveryCodesLeft,
        };
        this.$store.commit('SET_USUARIO_INFO', {
          ...this.getUsuarioInfo,
          mfaEnabled: true,
        });
        this.codigo = '';

        this.showAlert({
          type: 'success',
          title: '2FA ativado',
          message: 'A autenticação em dois fatores foi ativada com sucesso.',
          showConfirmButton: true,
        }).then(() => {
          // Usuário estava preso na tela de segurança: após ativar, libera a navegação
          this.$router.push({ name: 'seguranca' }).catch(() => { });
        });
      } catch (error) {
        await this._tratarErroMfa(error, 'ativar o 2FA');
      } finally {
        this.saving = false;
      }
    },
    async desativarMfa() {
      const confirm = await this.showConfirm({
        type: 'warning',
        title: 'Desativar 2FA?',
        message: 'O login usará apenas a senha até que a autenticação em dois fatores seja configurada novamente.',
      });

      if (!confirm.isConfirmed) {
        return;
      }

      this.disabling = true;
      try {
        const response = await http.post('usuario/Mfa/Disable');
        if (!response.data.succeeded) {
          throw new Error(response.data.message || 'Não foi possível desativar o 2FA.');
        }

        await this.carregarSetup();
        this.generatedRecoveryCodes = [];
        this.setup = {
          ...this.setup,
          mfaEnabled: false,
          recoveryCodesLeft: 0,
        };
        this.$store.commit('SET_USUARIO_INFO', {
          ...this.getUsuarioInfo,
          mfaEnabled: false,
        });

        this.showToast({
          type: 'success',
          title: '2FA desativado com sucesso.',
        });
      } catch (error) {
        await this._tratarErroMfa(error, 'desativar o 2FA');
      } finally {
        this.disabling = false;
      }
    },
  },
  async created() {
    await this.info();
    await this.carregarSetup();
  },
};
</script>

<style scoped>
.mfa-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  color: #666;
}

.mfa-status-badge {
  border-radius: 999px;
  padding: 8px 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.mfa-status-badge.is-enabled {
  background: #e7f7ee;
  color: #1b7f4f;
}

.mfa-status-badge.is-disabled {
  background: #fff1f0;
  color: #c0392b;
}

.mfa-summary {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.mfa-step-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 18px;
  background: #fff;
}

.mfa-step-title {
  display: flex;
  gap: 12px;
  margin-bottom: 15px;
}

.mfa-step-title h5 {
  margin-bottom: 6px;
  font-weight: 600;
}

.mfa-step-title p {
  margin-bottom: 0;
}

.mfa-step-number {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  background: #0783ae;
}

.security-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0;
}

.action-button {
  min-width: 220px;
}

.small-button {
  min-width: 170px;
}

.action-secondary {
  background: #f4f4f4;
  color: #333;
}

.action-danger {
  background: #d9534f;
}

.mfa-qr-wrapper {
  display: flex;
  justify-content: center;
  padding: 15px;
  background: #fafafa;
  border: 1px dashed #d8d8d8;
}

.mfa-key-group,
.mfa-verify-form {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.mfa-key-group .form-control,
.mfa-verify-form .form-control {
  flex: 1;
  min-width: 240px;
}

.recovery-alert {
  margin-top: 20px;
}

.recovery-list {
  margin: 12px 0 0;
  padding-left: 18px;
  column-count: 2;
}

@media (max-width: 768px) {

  .mfa-step-title,
  .mfa-key-group,
  .mfa-verify-form {
    flex-direction: column;
  }

  .action-button,
  .small-button {
    min-width: 100%;
  }

  .recovery-list {
    column-count: 1;
  }
}
</style>
